/**
 * Loading Spinner Component
 * @author Muhammad Arsan
 * @description A reusable Button component,  component using Material-UI's Button component
 */

import React from "react";
import CircularProgress from "@mui/material/CircularProgress";
import type { ButtonProps, ButtonVariant } from "./types/button";

const variantClasses: Record<ButtonVariant, string> = {
	primary: "bg-primary text-white hover:bg-green-700 font-semibold",
	secondary: "bg-green-50 text-primary hover:bg-green-100 font-semibold",
	icon: "text-black hover:text-primary p-1",
	tertiary: "bg-white font-semibold text-primary border border-primary hover:bg-gray-100",
	danger: "bg-red-500 text-white hover:bg-red-700",
	dangerSecondary: "bg-orange-500 text-white hover:bg-orange-600 font-semibold",
	dangerTertiary: "bg-white font-semibold text-orange-500 border border-orange-500 hover:bg-gray-100",
};

const Button: React.FC<ButtonProps> = React.memo(
	({
		text,
		variant = "primary",
		loading = false,
		isDisabled = false,
		className = "",
		disabled,
		children,
		...props
	}) => {
		const isButtonDisabled = loading || isDisabled || disabled;

		const baseClasses =
			variant === "icon"
				? "font-sans tracking-wide transition-colors duration-150 flex items-center justify-center"
				: "rounded-lg font-sans tracking-wide px-6 py-2 transition-colors duration-150 flex items-center justify-center gap-2";

		const hoverClasses = variant === "icon" ? "" : "hover:shadow-md";
		return (
			<button
				type="button"
				className={`${baseClasses} ${variantClasses[variant]} ${
					isButtonDisabled ? "opacity-60 cursor-not-allowed" : hoverClasses
				} ${className}`}
				disabled={isButtonDisabled}
				{...props}>
				{loading && <CircularProgress size={18} color="inherit" />}
				{children ? children : text}
			</button>
		);
	},
);

export default Button;
